import {
	CardConfig,
	CardType,
	type FilterCardConfig,
	type ImageCardConfig,
} from '@framework/analytics/models/card-model';
import type { ChartCardConfig } from '@framework/analytics/models/chart-config.model';
import type { CardList } from '@framework/analytics/models/layout.model';
import { DataService } from '@framework/analytics/services/data.service';
import {
	hasRenderableChartPayload,
	isSettledEmptyChartPayload,
} from '@framework/analytics/components/editors/jepto-chart-editor/services/chart-preview-data.util';
import {
	isDropdownFilterControlType,
	resolveFilterControlType,
} from '@framework/analytics/utils/filter-control-type.util';

const ASYNC_CAPTURE_CARD_TYPES = new Set<CardType>([
	CardType.Chart,
	CardType.Table,
	CardType.Map,
	CardType.Analysis,
]);

/** Whether a layout card must receive runtime data before capture is ready. */
export function cardNeedsAsyncCaptureData(
	card: CardList,
	config?: CardConfig | null
): boolean {
	if (ASYNC_CAPTURE_CARD_TYPES.has(card.cardType as CardType)) {
		return true;
	}

	if (card.cardType === CardType.Filter) {
		try {
			const controlType = resolveFilterControlType({
				layout: card,
				config: config as FilterCardConfig | undefined,
			});
			return isDropdownFilterControlType(controlType);
		} catch {
			return false;
		}
	}

	if (card.cardType === CardType.Html || card.cardType === CardType.Markdown) {
		return !config?.content?.trim();
	}

	if (card.cardType === CardType.Image) {
		const imageConfig = config as ImageCardConfig | undefined;
		return !(
			imageConfig?.imageRef?.trim() ||
			imageConfig?.imageRefLight?.trim() ||
			imageConfig?.imageRefDark?.trim()
		);
	}

	return false;
}

/** True when runtime data (or an error envelope) is already available for capture. */
export function cardHasCaptureData(
	card: CardList,
	config: CardConfig | null | undefined,
	dataService: DataService
): boolean {
	const cardId = card.cardId;

	switch (card.cardType) {
		case CardType.Chart: {
			const envelope = dataService.getQueryCardDataById(cardId);
			if (!envelope) {
				return false;
			}
			if (envelope.error) {
				return true;
			}
			const chartType =
				(config as ChartCardConfig | undefined)?.chartType ??
				('chartType' in envelope ? envelope.chartType : undefined);
			return (
				hasRenderableChartPayload(envelope, chartType) ||
				isSettledEmptyChartPayload(envelope, chartType)
			);
		}
		case CardType.Table:
		case CardType.Map:
		case CardType.Analysis:
		case CardType.Filter: {
			const envelope = dataService.getQueryCardDataById(cardId);
			return !!envelope && (!!envelope.data || !!envelope.error);
		}
		case CardType.Html:
		case CardType.Markdown: {
			if (config?.content?.trim()) {
				return true;
			}
			return !!dataService.getContentDataById(cardId)?.trim();
		}
		case CardType.Image: {
			const imageConfig = config as ImageCardConfig | undefined;
			if (
				imageConfig?.imageRef?.trim() ||
				imageConfig?.imageRefLight?.trim() ||
				imageConfig?.imageRefDark?.trim()
			) {
				return true;
			}
			const refs = dataService.imageDataSignal(cardId)();
			return !!(
				refs?.imageRef?.trim() ||
				refs?.imageRefLight?.trim() ||
				refs?.imageRefDark?.trim()
			);
		}
		default:
			return true;
	}
}

/**
 * Whether a runtime WebSocket event should mark the card settled for PDF capture.
 * Chart cards settle when the stored envelope is renderable, a settled empty
 * result, or an error.
 */
export function shouldSettleCaptureAfterWebSocket(
	cardType: CardType | string,
	card: CardList,
	config: CardConfig | null | undefined,
	dataService: DataService
): boolean {
	if (cardType === CardType.Chart || cardType === 'chart') {
		return cardHasCaptureData(card, config, dataService);
	}
	return true;
}
